import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { Fragment, useState } from 'react'
import { ChatBubbleOutlineRounded, SpeakerNotesOff } from '@mui/icons-material'
import { Badge, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { SPACING } from '@common/theme/spacing'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  minWidth: 600,
}

export type SiteCommentItem = {
  label: 'Adherence check' | 'Optional comment'
  text: string
}

export default function CommentModal({
  authorName,
  comment,
  requirementName,
}: {
  authorName?: string | null
  comment?: SiteCommentItem[]
  requirementName: string
}) {
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
  }

  const rows = comment?.filter((item) => item.text?.trim()) ?? []
  const hasComments = rows.length > 0
  const displayName = authorName?.trim() || '—'

  return (
    <Fragment>
      <Stack
        direction={'row'}
        spacing={SPACING.buttonSpacing}
        alignItems={'center'}
        justifyContent={'space-between'}
        alignSelf={'stretch'}
        width={'100%'}
        onClick={() => setOpen(true)}
        data-testid="open-site-comment-modal"
      >
        <Badge color="error" badgeContent={rows.length} invisible={!hasComments}>
          <ChatBubbleOutlineRounded sx={{ color: 'text.secondary', width: 20, height: 20 }} />
        </Badge>
      </Stack>
      <Modal
        open={open}
        onClose={handleClose}
        data-testid="comment-modal"
        aria-labelledby="comment-modal-title"
        aria-describedby="comment-modal-description"
      >
        <Paper sx={modalStyle}>
          <Stack width="100%">
            <Stack padding={3} spacing={SPACING.commentModalSpacing}>
              <Stack height={32}>
                {hasComments && (
                  <Typography variant="h6" color="text.primary" id="comment-modal-title">
                    Site's comment
                  </Typography>
                )}
              </Stack>
              <Stack height={24}>
                <Typography variant="body1" color="text.secondary" id="requirement-modal-title">
                  {requirementName}
                </Typography>
              </Stack>
            </Stack>
            <Box paddingX={3}>
              {hasComments ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 140 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 140 }}>Added by</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((item, index) => (
                        <TableRow key={`${index}-${item.label}`}>
                          <TableCell>{item.label}</TableCell>
                          <TableCell>{displayName}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={item.text}
                          >
                            {item.text}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Stack spacing={SPACING.minSpacing} width="100%" alignItems={'center'} py={4}>
                  <SpeakerNotesOff
                    sx={{
                      width: 48,
                      height: 48,
                      color: 'text.disabled',
                    }}
                  />
                  <Typography variant="h6" color="text.primary">
                    No comment added
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
          <Stack padding={SPACING.commentModalButtonSpacing} alignSelf={'stretch'} alignItems={'flex-end'}>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="close-comment-task-button"
            >
              Close
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </Fragment>
  )
}
